/**
 * A fund's return (or benchmark) since January, the way its workbook states it.
 *
 * Each fund sheet carries a "שנתי" block: the return and the benchmark since
 * January, worked out by the team on the sheet itself. That figure is the one
 * the team stands behind — it leaves out a row the sheet leaves out (a
 * manager handover written on a mid-month date) and carries a month the team
 * typed by hand. Compounding every stored row would put the handover back in
 * and show a different year from the sheet's, which is what the portal used
 * to do.
 *
 * So: the latest month that states a year-to-date figure anchors the year, and
 * the months after it — statement months, which state none — compound on top
 * of it. A fund whose sheet has no such block has no anchor, and every month
 * compounds, exactly as before.
 *
 * `rows` are one fund's months of one calendar year, up to the month shown.
 */
import type { PerformanceRow } from '@agio/core';

export type YearSeries = 'return' | 'benchmark';

const stated = (r: PerformanceRow, s: YearSeries) => (s === 'return' ? r.ytd_return : r.benchmark_ytd);
const monthly = (r: PerformanceRow, s: YearSeries) => (s === 'return' ? r.monthly_return : r.benchmark_monthly);

export function yearToDate(rows: PerformanceRow[], series: YearSeries): number {
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  let anchor = -1;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (stated(sorted[i], series) !== null) {
      anchor = i;
      break;
    }
  }
  let acc = anchor >= 0 ? 1 + stated(sorted[anchor], series)! : 1;
  for (let i = anchor + 1; i < sorted.length; i++) {
    const m = monthly(sorted[i], series);
    if (m !== null) acc *= 1 + m;
  }
  return acc - 1;
}
